import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';
import { cx } from './cx.js';

/**
 * Menu d'actions. Desktop : panneau flottant positionné par l'appelant
 * (`style`). Mobile (`sheet`) : panneau qui monte du bas avec un voile.
 * Se ferme avec Échap et au clic à l'extérieur.
 */
export function Menu({ label, onClose, sheet, style, className, children }: {
  label: string;
  onClose: () => void;
  sheet?: boolean;
  style?: CSSProperties | undefined;
  className?: string;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onPointer = (e: PointerEvent) => {
      // Le bouton qui a ouvert le menu (aria-expanded) le referme lui-même au clic.
      if ((e.target as Element).closest?.('[aria-expanded="true"]')) return;
      if (!sheet && ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // Au prochain tick : le clic qui a ouvert le menu ne doit pas le refermer.
    const t = setTimeout(() => window.addEventListener('pointerdown', onPointer), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [onClose, sheet]);

  if (sheet) {
    return (
      <div className="pl-sheet-layer">
        <div className="pl-sheet-veil" onClick={onClose} />
        <div ref={ref} className={cx('pl-sheet', className)} role="menu" aria-label={label} style={style}>
          <span className="pl-sheet-handle" aria-hidden="true" />
          {children}
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} className={cx('pl-menu', className)} role="menu" aria-label={label} style={style}>
      {children}
    </div>
  );
}

export function MenuItem({ icon, children, trailing, danger, checked, onSelect }: {
  icon?: IconName | ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  danger?: boolean;
  /** Présent = élément à choix unique (menuitemradio). */
  checked?: boolean;
  onSelect: () => void;
}) {
  const isRadio = checked !== undefined;
  return (
    <button
      type="button"
      role={isRadio ? 'menuitemradio' : 'menuitem'}
      aria-checked={isRadio ? checked : undefined}
      className={cx('pl-menu-item', danger && 'pl-menu-item-danger', checked && 'pl-menu-item-on')}
      onClick={onSelect}
    >
      {typeof icon === 'string' ? <Icon name={icon as IconName} size={16} className="pl-menu-icon" /> : icon}
      <span className="pl-menu-text">{children}</span>
      {trailing}
      {checked && <Icon name="check" size={14} className="pl-menu-check" />}
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="pl-menu-label">{children}</div>;
}

export function MenuSeparator() {
  return <div className="pl-menu-sep" role="separator" />;
}
